/**
 * The HTTP client, mirroring `packages/limuzin_core/lib/src/api.dart` so that the
 * web surfaces behave the way the two apps do: one refresh for many simultaneous
 * 401s, and every failure arriving as an {@link ApiError} carrying the server's
 * Russian message, which the UI shows verbatim rather than inventing its own.
 */

import { ServerClock, serverClock } from './server-clock';

/** The one error shape the server sends: `{ code, message, requestId }`. */
export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status?: number,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** True when the session is genuinely over rather than momentarily stale. */
  get isSignedOut(): boolean {
    return this.code === 'session_expired' || this.code === 'no_token';
  }
}

export interface Tokens {
  accessToken: string;
  refreshToken: string;
}

/**
 * Where the session lives between reloads.
 *
 * `localStorage` rather than an httpOnly cookie: the server issues bearer tokens to
 * the two mobile apps and this is the same door, and moving the panel to cookies
 * would mean a second session mechanism to keep correct. The trade is that a script
 * running on this origin can read the token — which is why the panel loads nothing
 * from a CDN and has no user-supplied HTML anywhere in it.
 *
 * [namespace] keys each surface separately, so a panel session and a customer
 * session on the same machine do not overwrite one another.
 */
export class TokenStore {
  constructor(private readonly namespace: string) {}

  /**
   * Whether the session outlives the tab. A passenger who unticks «Автоматический
   * вход» is kept in `sessionStorage`, which the browser empties when the tab
   * closes; everyone else in `localStorage`. Settled by whichever of the two the
   * session was found in, and by every `write` after that.
   */
  private remembered = true;

  private get key(): string {
    return `limuzin.${this.namespace}.session`;
  }

  read(): Tokens | null {
    for (const [storage, remembered] of [
      [globalThis.localStorage, true],
      [globalThis.sessionStorage, false],
    ] as const) {
      try {
        const raw = storage?.getItem(this.key);
        if (!raw) continue;
        const parsed = JSON.parse(raw) as Partial<Tokens>;
        if (!parsed.accessToken || !parsed.refreshToken) continue;
        this.remembered = remembered;
        return { accessToken: parsed.accessToken, refreshToken: parsed.refreshToken };
      } catch {
        // A private window, cleared site data, or a half-written value. No session.
      }
    }
    return null;
  }

  /** [remember] left out keeps the session where it already is — a refresh. */
  write(tokens: Tokens, remember: boolean = this.remembered): void {
    this.remembered = remember;
    const [keep, drop] = remember
      ? [globalThis.localStorage, globalThis.sessionStorage]
      : [globalThis.sessionStorage, globalThis.localStorage];
    try {
      drop?.removeItem(this.key);
      keep?.setItem(this.key, JSON.stringify(tokens));
    } catch {
      // Storage refused. The session still works until the tab closes.
    }
  }

  clear(): void {
    this.remembered = true;
    for (const storage of [globalThis.localStorage, globalThis.sessionStorage]) {
      try {
        storage?.removeItem(this.key);
      } catch {
        /* nothing to undo */
      }
    }
  }
}

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

/**
 * How long a request waits for the server to start answering before it is given up
 * as «Сервер не отвечает». `fetch` has no limit of its own: on a link that drops
 * packets rather than refusing them — a phone between two masts — it waits as long
 * as the browser does, which is minutes of a spinning button. Twenty seconds is the
 * Flutter client's own receive timeout, so both passenger apps give up alike.
 */
export const ANSWER_TIMEOUT_MS = 20_000;

/** The same for a file going up, which on a rural link takes its time honestly. */
export const UPLOAD_TIMEOUT_MS = 120_000;

/**
 * `fetch`, given up after [ms]. Every way of not getting an answer is an
 * {@link ApiError} with the code `offline`: nothing was refused, so the session
 * stands, and the words say which of the two it was. Whatever the answer, it says
 * what time it is, and that goes to [clock].
 */
async function fetchWithin(
  url: string,
  init: RequestInit,
  ms: number,
  clock: ServerClock,
): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  try {
    const sent = Date.now();
    const response = await fetch(url, { ...init, signal: controller.signal });
    clock.observeResponse(response, sent, Date.now());
    return response;
  } catch {
    throw controller.signal.aborted
      ? new ApiError('offline', 'Сервер не отвечает. Попробуйте ещё раз')
      : new ApiError('offline', 'Нет связи с сервером');
  } finally {
    clearTimeout(timer);
  }
}

export class Api {
  private tokens: Tokens | null;
  private refreshing: Promise<Tokens | null> | null = null;

  /** Called when refreshing fails: the session is genuinely over. */
  onSignedOut?: () => void;

  /**
   * [client] is what this app says it is on every request, as `x-limuzin-app` —
   * the passenger's web app `web/1.1.2`, so the panel can show which version a
   * passenger uses. The panel says nothing.
   *
   * Every answer's `Date` goes to [clock] — {@link serverClock} unless a test
   * says otherwise.
   */
  constructor(
    readonly baseUrl: string,
    private readonly store: TokenStore,
    private readonly client?: string,
    private readonly clock: ServerClock = serverClock,
  ) {
    this.tokens = store.read();
  }

  get isSignedIn(): boolean {
    return this.tokens !== null;
  }

  /**
   * [remember] false keeps the session for this tab alone — «Автоматический вход»
   * unticked. A refresh keeps it wherever it was.
   */
  adopt(tokens: Tokens, remember?: boolean): void {
    this.tokens = tokens;
    this.store.write(tokens, remember);
  }

  forget(): void {
    this.tokens = null;
    this.store.clear();
  }

  /**
   * The access token for the live channel's handshake, or null once there is no
   * session. The handshake has no 401 to retry on, so the socket passes back the
   * token the server has just `rejected` and this renews it as a 401 would: one
   * refresh shared with every request waiting on one, and none at all when the
   * session has rotated since that token was sent. A refresh that could not reach
   * the server hands back the same token, and the socket waits before asking again.
   */
  async accessToken(rejected?: string | null): Promise<string | null> {
    if (rejected && this.tokens?.accessToken === rejected) await this.refreshOnce();
    return this.tokens?.accessToken ?? null;
  }

  get<T>(path: string): Promise<T> {
    return this.send<T>('GET', path);
  }

  post<T>(path: string, body?: unknown): Promise<T> {
    return this.send<T>('POST', path, body);
  }

  patch<T>(path: string, body?: unknown): Promise<T> {
    return this.send<T>('PATCH', path, body);
  }

  put<T>(path: string, body?: unknown): Promise<T> {
    return this.send<T>('PUT', path, body);
  }

  delete<T>(path: string, body?: unknown): Promise<T> {
    return this.send<T>('DELETE', path, body);
  }

  /**
   * A photograph, which cannot be an `<img src>`: документы are streamed from a
   * route that wants an `Authorization` header, precisely so that a passport never
   * acquires a URL that works without a session. The caller gets an object URL and
   * is responsible for revoking it.
   */
  async blob(path: string, retry = true): Promise<Blob> {
    const used = this.tokens;
    const response = await this.raw('GET', path);
    if (response.status === 401 && retry && this.tokens) {
      if (this.tokens !== used) return this.blob(path, false);
      const refreshed = await this.refreshOnce();
      if (refreshed) return this.blob(path, false);
    }
    if (!response.ok) throw await this.errorFrom(response);
    return response.blob();
  }

  /**
   * A file, as `multipart/form-data` — a screenshot on its way to «Поддержка».
   *
   * Not `send`: that JSON-stringifies its body and names the content type, and a
   * multipart request needs the browser to set the type itself so it can put the
   * boundary in. The `FormData` is rebuilt for the retry, because a body that has
   * already been read cannot be sent a second time.
   */
  async upload<T>(path: string, file: File, retry = true): Promise<T> {
    const used = this.tokens;
    const form = new FormData();
    form.append('file', file);

    const response = await fetchWithin(
      `${this.baseUrl}${path}`,
      {
        method: 'POST',
        headers: this.headers(),
        body: form,
      },
      UPLOAD_TIMEOUT_MS,
      this.clock,
    );

    if (response.status === 401 && retry && this.tokens) {
      if (this.tokens !== used) return this.upload<T>(path, file, false);
      const refreshed = await this.refreshOnce();
      if (refreshed) return this.upload<T>(path, file, false);
    }
    if (!response.ok) throw await this.errorFrom(response);
    return (await response.json()) as T;
  }

  private async send<T>(method: Method, path: string, body?: unknown, retry = true): Promise<T> {
    const used = this.tokens;
    const response = await this.raw(method, path, body);

    if (response.status === 401 && retry && this.tokens) {
      // A request sent with a token that has since been replaced is not evidence of
      // anything — somebody else refreshed while this one was in flight. Retry with
      // what is current rather than rotating again: rotation is single-use on the
      // server, so a second one would revoke the session that just started working.
      if (this.tokens !== used) return this.send<T>(method, path, body, false);

      // One refresh for many simultaneous 401s: whoever gets there first does the
      // work and everyone else waits on the same promise.
      const refreshed = await this.refreshOnce();
      if (refreshed) return this.send<T>(method, path, body, false);
    }

    if (!response.ok) throw await this.errorFrom(response);
    if (response.status === 204) return undefined as T;

    const text = await response.text();
    return (text ? JSON.parse(text) : null) as T;
  }

  private raw(method: Method, path: string, body?: unknown): Promise<Response> {
    return fetchWithin(
      `${this.baseUrl}${path}`,
      {
        method,
        headers: {
          ...(body === undefined ? {} : { 'content-type': 'application/json' }),
          ...this.headers(),
        },
        body: body === undefined ? undefined : JSON.stringify(body),
      },
      ANSWER_TIMEOUT_MS,
      this.clock,
    );
  }

  private headers(): Record<string, string> {
    return {
      ...(this.client ? { 'x-limuzin-app': this.client } : {}),
      ...(this.tokens ? { authorization: `Bearer ${this.tokens.accessToken}` } : {}),
    };
  }

  private async errorFrom(response: Response): Promise<ApiError> {
    let payload: unknown = null;
    try {
      const text = await response.text();
      payload = text ? JSON.parse(text) : null;
    } catch {
      /* not JSON — fall through to the generic message */
    }
    if (payload && typeof payload === 'object' && 'code' in payload) {
      const body = payload as { code: string; message?: string; requestId?: string };
      return new ApiError(
        body.code,
        body.message ?? 'Что-то пошло не так',
        response.status,
        body.requestId,
      );
    }
    return new ApiError(`http_${response.status}`, 'Что-то пошло не так', response.status);
  }

  private async refreshOnce(): Promise<Tokens | null> {
    this.refreshing ??= this.refresh();
    const result = await this.refreshing;
    this.refreshing = null;
    return result;
  }

  private async refresh(): Promise<Tokens | null> {
    const current = this.tokens;
    if (!current) return null;

    let response: Response;
    try {
      response = await fetchWithin(
        `${this.baseUrl}/api/auth/refresh`,
        {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ refreshToken: current.refreshToken }),
        },
        ANSWER_TIMEOUT_MS,
        this.clock,
      );
    } catch {
      // A refresh that failed on the network is not a refresh that was refused —
      // keep the session and let the caller surface the connection error.
      return null;
    }

    if (response.ok) {
      const next = (await response.json()) as Tokens;
      this.adopt(next);
      return next;
    }

    this.forget();
    this.onSignedOut?.();
    return null;
  }
}
